import { useState } from "react";
import { toast } from "sonner";
import { Check, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const TIERS = [
  { amount: 25, name: "Equipment Pack", impact: "Puts a ball, jersey, and gear bag in a kid's hands." },
  { amount: 100, name: "Season Sponsor", impact: "Covers one athlete's full season — fees, travel, uniform." },
  { amount: 500, name: "Mentorship Grant", impact: "Funds a year of one-on-one mentorship for one youth." },
];

export const Donate = () => {
  const [selected, setSelected] = useState(100);
  const [custom, setCustom] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const amount = custom ? Number(custom) || 0 : selected;

  const pledge = async () => {
    if (!amount || amount <= 0) {
      toast.error("Pick a tier or enter an amount first.");
      return;
    }
    if (!email) {
      toast.error("Add your email for the receipt.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/donations/checkout", {
        amount,
        email,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start secure checkout. Please try again.");
      setBusy(false);
    }
  };

  return (
    <section id="donate" className="py-20 sm:py-28 lg:py-36 px-4 sm:px-8 lg:px-16 relative overflow-hidden" data-testid="donate-section">
      <div className="absolute inset-0 -z-10" style={{ background: "radial-gradient(ellipse 55% 45% at 50% 0%, rgba(139,92,246,0.14), transparent 65%)" }} />
      <Reveal>
        <div className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-[#FFB800] mb-4">Fuel The Movement · Nonprofit</div>
        <h2 className="font-display font-black uppercase tracking-tight leading-none text-3xl sm:text-4xl lg:text-6xl text-[#F4F4F6] max-w-4xl">
          Your dollars become <span className="text-[#8B5CF6]">reps, mentors & second chances.</span>
        </h2>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
        {TIERS.map((t, i) => {
          const active = !custom && selected === t.amount;
          return (
            <Reveal key={t.amount} delay={i * 0.08}>
              <button
                onClick={() => { setSelected(t.amount); setCustom(""); }}
                data-testid={`donate-tier-${t.amount}`}
                className={`w-full text-left p-8 border transition-all duration-300 active:scale-[0.98] ${
                  active
                    ? "border-[#8B5CF6] bg-[#8B5CF6]/10 shadow-[0_0_40px_rgba(255,59,29,0.15)]"
                    : "border-[#242436] bg-[#161622] hover:border-[#3D3D58]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-display font-black text-4xl text-[#F4F4F6]">${t.amount}</span>
                  <span className={`w-6 h-6 border flex items-center justify-center transition-colors duration-200 ${active ? "border-[#8B5CF6] bg-[#8B5CF6]" : "border-[#3D3D58]"}`}>
                    {active && <Check className="w-4 h-4 text-black" strokeWidth={3} />}
                  </span>
                </div>
                <div className="font-display font-bold uppercase tracking-wide text-lg text-[#FFB800] mt-3">{t.name}</div>
                <p className="text-sm leading-relaxed text-[#A1A1B3] mt-2">{t.impact}</p>
              </button>
            </Reveal>
          );
        })}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <span className="absolute left-5 top-1/2 -translate-y-1/2 font-display font-bold text-xl text-[#62627A]">$</span>
            <input
              type="number"
              min="1"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Custom amount"
              data-testid="donate-custom-amount"
              className="w-full bg-[#161622] border border-[#242436] focus:border-[#8B5CF6] outline-none text-[#F4F4F6] font-display font-bold text-xl pl-10 pr-5 py-4 placeholder:text-[#62627A] placeholder:font-body placeholder:text-sm transition-colors duration-200"
            />
          </div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email *"
            data-testid="donate-email-input"
            className="flex-1 bg-[#161622] border border-[#242436] focus:border-[#8B5CF6] outline-none text-[#F4F4F6] text-sm px-4 py-4 placeholder:text-[#62627A] transition-colors duration-200"
          />
          <button
            onClick={pledge}
            disabled={busy}
            data-testid="donate-submit-button"
            className="bg-[#8B5CF6] text-black font-display font-extrabold uppercase tracking-wider text-lg px-12 py-4 flex items-center justify-center gap-2 hover:bg-[#FFB800] active:scale-95 disabled:opacity-50 transition-colors duration-200"
          >
            <Zap className="w-5 h-5" strokeWidth={2.5} />
            {busy ? "Opening secure checkout..." : `Donate${amount > 0 ? ` $${amount.toLocaleString()}` : ""}`}
          </button>
        </div>
        <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#62627A] mt-4">
          Secure checkout by Stripe — cards, Apple Pay & more. G.A.M.E. Mentorship and Training Inc. is a 501(c)(3) tax-exempt organization — donations are tax-deductible to the extent allowed by law and are processed separately from G.A.M.E. Training LLC activity.
        </p>
      </Reveal>
    </section>
  );
};
